// InvoiceView.jsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { getInvoiceById } from "../../services/invoiceService";

const formatCurrency = (value) => {
  if (!value) return "R$ 0,00";
  const numValue = Number(value);
  return numValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const InvoiceView = ({ invoiceId, onClose }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await getInvoiceById(invoiceId);
        setInvoice(data);
      } catch (err) {
        setError("Erro ao carregar detalhes da nota fiscal");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <p className="text-gray-600">Carregando detalhes da nota fiscal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <p className="text-red-600">{error}</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  // Tratamento para extrair os itens da nota (array ou objeto único)
  const items = Array.isArray(invoice.CorpoNotaFiscal)
    ? invoice.CorpoNotaFiscal
    : invoice.CorpoNotaFiscal
    ? [invoice.CorpoNotaFiscal]
    : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 no-print">
      <div className="relative top-6 mx-auto w-full max-w-6xl mb-6 px-4">
        <div className="bg-white shadow-lg rounded-lg">
          {/* Cabeçalho */}
          <div className="flex md:flex-row items-center justify-between p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-700">
              Nota Fiscal #{invoice.NUMERO.trim()}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6">
            {/* Informações da Nota Fiscal */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Informações da Nota Fiscal
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Vendedor
                    </label>
                    <p className="mt-1">{invoice.VENDEDOR_CODIGO} - {invoice.VENDEDOR_NOME}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Cliente
                    </label>
                    <p className="mt-1">{invoice.CLIENTE_CODIGO} - {invoice.CLIENTE_NOME}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Pedido
                    </label>
                    <p className="mt-1">#{invoice.PEDIDO_CODIGO || "-"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Data de Emissão
                    </label>
                    <p className="mt-1">{formatDate(invoice.DATA_EMISSAO)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Tipo de Frete
                    </label>
                    <p className="mt-1">{invoice.TIPO_FRETE}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Chave NFe
                    </label>
                    <p className="mt-1 break-all">
                      {invoice.CHAVE_NFE.trim() || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Data de Entrega
                    </label>
                    <p className="mt-1">{formatDate(invoice.DATA_ENTREGA)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Transportadora
                    </label>
                    <p className="mt-1">{invoice.TRANSPORTADORA_CODIGO} - {invoice.TRANSPORTADORA_NOME}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Peso Líquido
                    </label>
                    <p className="mt-1">{invoice.PESO_TOTAL_LIQUIDO} kg</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Peso Bruto
                    </label>
                    <p className="mt-1">{invoice.PESO_TOTAL_BRUTO} kg</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Valores */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Valores</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total da Nota
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_NOTA_FISCAL)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total de Produtos
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_PRODUTOS)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total IPI
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_IPI)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total ICMS
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_ICMS)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Base ICMS
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.BASE_ICMS)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total ST
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_ST)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total Seguro
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_SEGURO)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Valor Total Desconto
                    </label>
                    <p className="mt-1 font-medium">
                      {formatCurrency(invoice.VALOR_TOTAL_DESCONTO)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Itens da Nota */}
            {items.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Itens da Nota
                </h3>
                <div className="rounded-lg p-4">
                  <div className="hidden md:block">
                    <table className="w-full border rounded-lg bg-gray-50 ">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Produto
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Quantidade
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Valor Unitário
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Valor Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-50">
                        {items.map((entry, index) => {
                          const item = entry.Item;
                          return (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-3">{item.PRODUTO_CODIGO.trim()}</td>
                              <td className="px-4 py-3">
                                {Number(item.QUANTIDADE_TOTAL_FATURAMENTO).toLocaleString("pt-BR")}
                              </td>
                              <td className="px-4 py-3">
                                {formatCurrency(item.VALOR_UNITARIO_FATURAMENTO)}
                              </td>
                              <td className="px-4 py-3">
                                {formatCurrency(item.VALOR_TOTAL_FATURAMENTO)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="block md:hidden space-y-4">
                    {items.map((entry, index) => {
                      const item = entry.Item;
                      return (
                        <div key={index} className="border rounded-lg p-4 shadow-sm">
                          <p className="font-medium text-sm">{item.PRODUTO_CODIGO.trim()}</p>
                          <p className="text-xs text-gray-500">
                            Quantidade:{" "}
                            {Number(item.QUANTIDADE_TOTAL_FATURAMENTO).toLocaleString("pt-BR")}
                          </p>
                          <p className="text-xs text-gray-500">
                            Valor Unitário: {formatCurrency(item.VALOR_UNITARIO_FATURAMENTO)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Valor Total: {formatCurrency(item.VALOR_TOTAL_FATURAMENTO)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Mensagem da Nota (se houver) */}
            {invoice.MENSAGEM_NOTA_FISCAL.trim() && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Mensagem da Nota
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {invoice.MENSAGEM_NOTA_FISCAL.trim()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Rodapé */}
          <div className="mt-6 pt-4 border-t flex justify-end p-6 no-print">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
